import posthog from 'posthog-js';
import { HEARD_ABOUT_ANSWERED_FLAG, type HeardAboutAnswer } from '@puzzlio/core';
import { setHeardAbout } from '@/app/actions/profile';
import { trackHeardAboutSubmitted } from './analytics-events';
import { LocalStorageKeys } from './constants';

/** This browser's answer, if it has given one. */
export const storedHeardAbout = (): HeardAboutAnswer | undefined => {
  try {
    const stored = localStorage.getItem(LocalStorageKeys.HEARD_ABOUT);
    return stored ? JSON.parse(stored) : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Only worth asking when PostHog will record the answer: without analytics consent it isn't even capturing.
 * Reads PostHog's own opt-in rather than the consent store, as that also covers PostHog not being loaded.
 */
export const canAskHeardAbout = () =>
  posthog.__loaded &&
  posthog.has_opted_in_capturing() &&
  !storedHeardAbout() &&
  !posthog.isFeatureEnabled(HEARD_ABOUT_ANSWERED_FLAG, { send_event: false });

/**
 * Keeps the answer on an account's profile, and notes in this browser that it's there. `ifUnset` is for
 * an answer given while signed out, which mustn't replace one the account gave elsewhere.
 */
const saveToProfile = async (answer: HeardAboutAnswer, userId: string, ifUnset: boolean) => {
  try {
    const result = await setHeardAbout(answer, ifUnset);
    if (result.error) {
      console.error('Saving heard-about failed:', result.error);
      return;
    }
    localStorage.setItem(LocalStorageKeys.HEARD_ABOUT_SAVED_FOR, userId);
  } catch (error) {
    console.error('Saving heard-about failed:', error);
  }
};

/**
 * The answer becomes person properties, but only for a signed-in player: on an anonymous one it would create a
 * billable person profile (see PostHogInit). Theirs waits in this browser and rides along with the identify call
 * when they sign in; until then it's on the event. A signed-in player's answer is kept on their profile too,
 * and a signed-out one's when they sign in (see saveStoredHeardAbout).
 */
export const recordHeardAbout = (answer: HeardAboutAnswer, userId: string | undefined) => {
  localStorage.setItem(LocalStorageKeys.HEARD_ABOUT, JSON.stringify(answer));
  trackHeardAboutSubmitted(answer);
  if (userId) {
    posthog.setPersonProperties(answer);
    void saveToProfile(answer, userId, false);
  }
};

/** At sign-in: an answer this browser holds goes onto the account's profile, once, if it has none. */
export const saveStoredHeardAbout = (userId: string) => {
  const answer = storedHeardAbout();
  if (!answer) return;
  try {
    if (localStorage.getItem(LocalStorageKeys.HEARD_ABOUT_SAVED_FOR) === userId) return;
  } catch {
    return;
  }
  void saveToProfile(answer, userId, true);
};
